import { forwardRef, useId } from "react";
import { cn } from "@/lib/cn";

// Labeled text field with optional leading icon + error message. Used in auth.
const Input = forwardRef(function Input(
  { label, icon: Icon, error, className, id, required, ...props },
  ref
) {
  // The error was rendered as a plain sibling: nothing marked the field invalid
  // and nothing pointed at the message, so a screen reader announced the box as
  // valid and never read why the form wouldn't submit. Falls back to a generated
  // id when the caller didn't give one, so the association always holds.
  const autoId = useId();
  const fieldId = id || autoId;
  const errorId = `${fieldId}-error`;
  return (
    <label className="block" htmlFor={fieldId}>
      {label && (
        <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
          {label}
          {/* The required marker is the LABEL's job, not a separate legend: a
              field that must be filled has to say so where it is read. Matches
              the convention already used on the exe importer's field list.
              aria-hidden because `aria-required` below is what a screen reader
              announces — reading a bare "star" adds nothing. */}
          {required && <span className="text-rose-500" aria-hidden="true"> *</span>}
        </span>
      )}
      <span className="relative block">
        {Icon && (
          <Icon
            size={18}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
        )}
        <input
          ref={ref}
          id={fieldId}
          // Forwarded exactly as it was when `required` arrived through the prop
          // spread, so nothing that already set it changes behaviour.
          required={required}
          aria-required={required ? "true" : undefined}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            "h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500",
            Icon && "pl-11",
            error && "border-rose-400 focus:border-rose-500 focus:ring-rose-500/15",
            className
          )}
          {...props}
        />
      </span>
      {error && (
        <span id={errorId} role="alert" className="mt-1.5 block text-xs font-medium text-rose-500">
          {error}
        </span>
      )}
    </label>
  );
});

export default Input;
